<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import type { RouteLocationRaw } from "vue-router";

import type { FollowingSection } from "@/components/following/helpers";
import type { FollowingListItem } from "@/components/following/types";

import ButtonAlt from "@/dsComponents/buttons/ButtonAlt.vue";

import ContentBlock from "@/components/following/ContentBlock.vue";
import FollowingLine from "@/components/following/FollowingLine.vue";
import FollowingListSection from "@/components/following/FollowingListSection.vue";
import { toFollowingLineProps } from "@/components/following/helpers";

interface FollowingListProps {
    sections?: FollowingSection[];
    gap?: string;
    onLinkNavigate?: (to: RouteLocationRaw, event: MouseEvent) => void;
    onNoteLineClick?: (payload: { initialContent: string; noteId?: string; eventId: string }) => void;
    currentCollaboratorId?: string | null;
    canEditInterviews?: boolean;
    onInterviewEditClick?: (payload: { interviewId: string; summary: string }) => void;
}

const { t } = useI18n();

const props = withDefaults(defineProps<FollowingListProps>(), {
    sections: () => [],
    gap: "var(--spacing-xl, 16px)",
    onLinkNavigate: undefined,
    onNoteLineClick: undefined,
    currentCollaboratorId: undefined,
    canEditInterviews: false,
    onInterviewEditClick: undefined,
});

const hasEntries = computed(() => props.sections.some((section) => section.items.length > 0));

const listStyle = computed(() => ({ gap: props.gap }));

const PROFILE_HISTORY_LIST_ITEM_PREFIX = "profile-history-";

const resolveNoteIdForNoteLine = (item: FollowingListItem): string | undefined => {
    const fromContext = item.noteContext?.noteId?.trim();
    if (fromContext) return fromContext;
    if (item.icon !== "note") return undefined;
    const rowId = item.id?.trim();
    if (rowId && !rowId.startsWith(PROFILE_HISTORY_LIST_ITEM_PREFIX)) return rowId;
    return undefined;
};

const isCurrentUserNoteAuthor = (item: FollowingListItem): boolean => {
    const viewerId = props.currentCollaboratorId?.trim();
    const authorId = item.noteContext?.logCollaboratorId;
    if (!viewerId || authorId === undefined || authorId === null) return false;
    return String(authorId).trim() === viewerId;
};

const isNoteLineClickable = (item: FollowingListItem): boolean => {
    if (!props.onNoteLineClick || item.icon !== "note" || !item.content) return false;
    return isCurrentUserNoteAuthor(item);
};

const handleNoteLineActivate = (item: FollowingListItem) => {
    if (!isNoteLineClickable(item) || !props.onNoteLineClick) return;
    props.onNoteLineClick({
        initialContent: item.content ?? "",
        noteId: resolveNoteIdForNoteLine(item),
        eventId: item.id,
    });
};

const isInterviewLineEditable = (item: FollowingListItem): boolean =>
    props.canEditInterviews && !!props.onInterviewEditClick && !!item.interviewContext;

const handleInterviewLineEdit = (item: FollowingListItem) => {
    if (!item.interviewContext || !isInterviewLineEditable(item)) return;
    props.onInterviewEditClick?.({ interviewId: item.interviewContext.interviewId, summary: item.content ?? "" });
};
</script>

<template>
    <div v-if="hasEntries" class="following-list" :style="listStyle" role="list">
        <FollowingListSection v-for="section in props.sections" :key="section.title" :title="section.title">
            <FollowingLine
                v-for="(item, itemIndex) in section.items"
                :key="item.id"
                v-bind="toFollowingLineProps(item)"
                :on-link-navigate="props.onLinkNavigate"
                :show-thread-line="itemIndex < section.items.length - 1"
            >
                <template v-if="isNoteLineClickable(item) || isInterviewLineEditable(item)" #right>
                    <ButtonAlt
                        size="sm"
                        type="neutral"
                        variant="ghost"
                        left-icon="edit"
                        :label="t('global.actions.edit')"
                        @click="isNoteLineClickable(item) ? handleNoteLineActivate(item) : handleInterviewLineEdit(item)"
                    />
                </template>
                <template v-if="item.content" #bottom>
                    <ContentBlock :content="item.content" />
                </template>
            </FollowingLine>
        </FollowingListSection>
    </div>
    <p v-else class="following-list__empty typo-xxs-regular">
        {{ t("following.empty") }}
    </p>
</template>

<style scoped lang="scss">
.following-list {
    display: flex;
    flex-direction: column;
}

.following-list__empty {
    margin: 0;
    padding: var(--spacing-xl, 16px) var(--spacing-lg, 12px);
    color: var(--text-sub-600, #494e61);
}
</style>
